/**
 * Permission codes the frontends reference, and the one function that answers "may they?".
 *
 * The list is not the catalogue — the server owns that, and the user's effective set arrives with
 * the session. These are only the codes this code branches on, named so a typo is a compile
 * error rather than a control that silently never renders.
 */

export const Permission = {
  /**
   * Rule 9: the single gate on every cost, margin and profit figure in the system. There is
   * exactly one, checked in exactly one place, and the backend has an architecture test that
   * fails the build if a second cost-implying permission is ever added.
   */
  CostView: 'cost.view',

  SaleCreate: 'sale.create',
  SaleViewOwn: 'sale.view.own',
  SaleViewAll: 'sale.view.all',
  SaleRequestReversal: 'sale.reversal.request',
  SaleReprintReceipt: 'sale.receipt.reprint',

  // Payments. `PaymentConfirmManual` is owner-only and the client checks it before rendering
  // the control, so nobody is ever offered an action that will be refused.
  PaymentView: 'payment.view',
  PaymentRecord: 'payment.record',
  PaymentInitiateMpesa: 'payment.mpesa.initiate',
  PaymentEnterCode: 'payment.code.enter',
  PaymentConfirmManual: 'payment.confirm_manual',
  PaymentRefund: 'payment.refund',
  PaymentUnmatchedView: 'payment.unmatched.view',
  PaymentUnmatchedResolve: 'payment.unmatched.resolve',
  PaymentReconcileView: 'payment.reconcile.view',
  CustomerCreditView: 'customer_credit.view',
  CustomerCreditManage: 'customer_credit.manage',

  // Cash accountability.
  ShiftOpen: 'shift.open',
  ShiftClose: 'shift.close',
  ShiftViewOwn: 'shift.view.own',
  ShiftViewAll: 'shift.view.all',
  ShiftApproveVariance: 'shift.approve_variance',
  HandoverDeclare: 'handover.declare',
  HandoverReceive: 'handover.receive',
  CashPayout: 'cash.payout',
  CashViewPosition: 'cash.position.view',

  CategoryView: 'category.view',
  CategoryManage: 'category.manage',
  CategoryManageAttributes: 'category.manage_attributes',
  ProductView: 'product.view',
  ProductCreate: 'product.create',
  ProductEdit: 'product.edit',
  ProductDeactivate: 'product.deactivate',
  VariantCreate: 'variant.create',
  VariantEdit: 'variant.edit',
  StockView: 'stock.view',
  StockAdjust: 'stock.adjust',
  StockOpeningBalance: 'stock.opening_balance',
  SupplierView: 'supplier.view',
  SupplierManage: 'supplier.manage',
  PurchaseView: 'purchase.view',
  PurchaseCreate: 'purchase.create',
  PurchaseConfirm: 'purchase.confirm',
  UnitCreate: 'unit.create',
  UnitView: 'unit.view',
  PriceView: 'price.view',
  PriceEdit: 'price.edit',
  PriceSet: 'price.set',
  PriceSetFloor: 'price.set_floor',
  PriceViewHistory: 'price.view_history',
  ImportRun: 'import.run',
  ImportTemplate: 'import.template',
  ImportDryRun: 'import.dry_run',
  ImportCommit: 'import.commit',
  LabelExport: 'label.export',
  UserView: 'user.view',
  UserCreate: 'user.create',
  UserEdit: 'user.edit',
  UserDeactivate: 'user.deactivate',
  UserRevokeDevices: 'user.revoke_devices',
  RoleView: 'role.view',
  RoleCreate: 'role.create',
  RoleEdit: 'role.edit',
  RoleAssignPermissions: 'role.assign_permissions',
  ShopView: 'shop.view',
  ShopCreate: 'shop.create',
  ShopEdit: 'shop.edit',
  /** Read or write stock, units and movements at a shop other than your own home shop. */
  ShopActAny: 'shop.act_any',
  SettingView: 'setting.view',
  AuditView: 'audit.view',
  SettingsView: 'settings.view',
  SettingsEdit: 'settings.edit',

  // ── Phase 4: stock integrity ──────────────────────────────────────────────
  // The four owner-only ones are marked. They are not merely "sensitive": each is the last point
  // at which somebody could still say no, so the UI must not render a control for them to a role
  // that lacks them — an offered button that 403s reads as a broken screen, not as a boundary.

  StockMoveToFaulty: 'stock.move_to_faulty',

  TransferView: 'transfer.view',
  TransferCreate: 'transfer.create',
  TransferDispatch: 'transfer.dispatch',
  TransferReceive: 'transfer.receive',
  TransferResolveDiscrepancy: 'transfer.resolve_discrepancy',
  /** Owner only. Writing off stock lost in transit admits the goods are gone. */
  TransferApproveLoss: 'transfer.approve_loss',

  StockCountView: 'stockcount.view',
  StockCountCreate: 'stockcount.create',
  StockCountEnter: 'stockcount.enter',
  StockCountSubmit: 'stockcount.submit',
  StockCountApprove: 'stockcount.approve',
  /** Owner only, above the configured variance threshold. */
  StockCountApproveHighVariance: 'stockcount.approve_high_variance',

  WriteOffView: 'writeoff.view',
  WriteOffRequest: 'writeoff.request',
  /** Owner only, with no threshold — the one control that stops stock quietly disappearing. */
  WriteOffApprove: 'writeoff.approve',

  OnlineView: 'online.view',
  OnlineListingManage: 'online.listing.manage',
  /** The storefront itself — name, web address, pickup shops. Changes what every customer sees. */
  OnlineStorefrontManage: 'online.storefront.manage',
  /** Includes the customer's name and phone number. */
  OnlineOrderView: 'online.order.view',
  OnlineOrderHandle: 'online.order.handle',

  ClaimView: 'claim.view',
  ClaimCreate: 'claim.create',
  ClaimUpdate: 'claim.update',
  ClaimResolve: 'claim.resolve',

  SettlementView: 'settlement.view',
  /** Owner only: accepting a settlement decides which shop's books carry the stock. */
  SettlementResolve: 'settlement.resolve',

  PriceBulkExport: 'price.bulk_export',
  PriceBulkUpdate: 'price.bulk_update',
  /** Owner only, past the configured batch size. A misplaced decimal on two hundred rows. */
  PriceBulkUpdateLarge: 'price.bulk_update_large',

  SaleApproveReversal: 'sale.reversal.approve',

  // Phase 5 — oversight.
  ReviewView: 'review.view',
  ReviewResolve: 'review.resolve',
  /** Owner only — dismissing is a judgement call about what matters, not a routine act. */
  ReviewDismiss: 'review.dismiss',

  DisputeCreate: 'dispute.create',
  DisputeResolve: 'dispute.resolve',

  ApprovalRequest: 'approval.request',
  ApprovalDecide: 'approval.decide',
  ApprovalDecideOwnerLevel: 'approval.decide_owner_level',

  BrokerView: 'broker.view',
  BrokerManage: 'broker.manage',

  CommissionViewOwn: 'commission.view_own',
  CommissionViewAll: 'commission.view_all',
  /** Owner only — a rule change moves future pay. */
  CommissionRuleManage: 'commission.rule_manage',
  CommissionPayoutCreate: 'commission.payout_create',
  /** Owner only — marking a payout paid is admitting real money left. */
  CommissionPayoutApprove: 'commission.payout_approve',

  CustomerView: 'customer.view',
  CustomerManage: 'customer.manage',

  ReportView: 'report.view',
  DashboardView: 'dashboard.view',

  // ── Phase 6: printing and notifications ──────────────────────────────────
  /** Owner-level: issuing an agent hands out a long-lived, bearer-like token. */
  PrintAgentManage: 'printagent.manage',
  PrinterManage: 'printer.manage',
  PrintJobView: 'printjob.view',
  PrintJobRetry: 'printjob.retry',
  PrintJobCancel: 'printjob.cancel',
  NotificationManage: 'notification.manage',
} as const;

export type PermissionCode = (typeof Permission)[keyof typeof Permission];

/** What the check reads. Kept structural so it works with the session slice or a bare list. */
export type PermissionHolder = { readonly permissions: readonly string[] } | null | undefined;

/** Every listed permission is required, never any of them — matching the server's filter. */
export function can(holder: PermissionHolder, ...required: readonly string[]): boolean {
  if (!holder) return false;
  const held = holder.permissions;
  return required.every((code) => held.includes(code));
}

/** True when the actor may see cost, margin or profit anywhere. The only caller of this concept. */
export function canSeeCost(holder: PermissionHolder): boolean {
  return can(holder, Permission.CostView);
}
